import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { TspModal } from "../../../components/primitives";
import TspApiService from "../../../api/tspApiService";
import { tspErrorMessage } from "../../../hooks/useTspApiResource";

// The typed latitude / longitude as numbers, or { error } (both are required).
export function parseStopPoint(latitude, longitude) {
  const lat = String(latitude ?? "").trim();
  const lng = String(longitude ?? "").trim();
  if (!lat || !lng) return { error: "Enter both latitude and longitude." };
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || latNum < -90 || latNum > 90) return { error: "Latitude must be a number between -90 and 90." };
  if (!Number.isFinite(lngNum) || lngNum < -180 || lngNum > 180) return { error: "Longitude must be a number between -180 and 180." };
  return { point: { latitude: latNum, longitude: lngNum } };
}

// "Update Location" popup for one stop of the Update Journey timeline: on open,
// the stop's latitude / longitude are fetched for its place (the server looks
// it up — nothing is saved yet) into the two fields. They can then be changed
// by hand (or typed in when nothing was found); Save stores what the fields
// hold (the server checks the point lies in the place's state).
// `onSave(city, { latitude, longitude })` returns { ok } | { ok: false, message }.
export default function StopLocationModal({ open, journeyId, city, onClose, onSave }) {
  const [draft, setDraft] = useState({ latitude: "", longitude: "" });
  const [approximate, setApproximate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchPoint = useCallback(async () => {
    if (!city) return;
    setLoading(true);
    setError("");
    setApproximate(false);
    try {
      const response = await TspApiService.locateRouteCity(journeyId, city.id);
      const found = response?.data;
      if (found) {
        setDraft({ latitude: String(found.latitude), longitude: String(found.longitude) });
        setApproximate(Boolean(found.approximate));
      }
    } catch (problem) {
      setError(`${tspErrorMessage(problem, "Could not fetch the location of this place.")} You can enter it by hand.`);
    } finally {
      setLoading(false);
    }
  }, [journeyId, city]);

  // Each opening starts from the saved point (if any), then fetches.
  useEffect(() => {
    if (!open || !city) return;
    setDraft(city.hasLocation ? { latitude: String(city.latitude), longitude: String(city.longitude) } : { latitude: "", longitude: "" });
    fetchPoint();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening
  }, [open, city]);

  const change = (key) => (event) => {
    setDraft((current) => ({ ...current, [key]: event.target.value }));
    setApproximate(false);
    setError("");
  };

  const save = async () => {
    const checked = parseStopPoint(draft.latitude, draft.longitude);
    if (checked.error) {
      setError(checked.error);
      return;
    }
    setSaving(true);
    const result = await onSave(city, checked.point);
    setSaving(false);
    if (result && result.ok === false) {
      setError(result.message || "Could not save the location.");
      return;
    }
    onClose();
  };

  const saved = city && city.hasLocation ? `${city.latitude}, ${city.longitude}` : "Not set";
  const busy = loading || saving;

  return (
    <TspModal
      open={open}
      title={`Update Location${city ? ` — ${city.name}` : ""}`}
      size="sm"
      onClose={saving ? undefined : onClose}
      footer={
        <>
          <button type="button" className="tsp-btn tsp-btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="tsp-btn tsp-btn-primary" onClick={save} disabled={busy}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <p className="tsp-muted-text tsp-stop-location-current">Saved location: {saved}</p>
      <div className="tsp-stop-location-grid">
        <div className="tsp-form-field">
          <label htmlFor="tsp-stop-location-lat">Latitude</label>
          <input id="tsp-stop-location-lat" type="text" inputMode="decimal" className="tsp-input" placeholder={loading ? "Fetching…" : "e.g. 21.0393"} value={draft.latitude} onChange={change("latitude")} disabled={busy} />
        </div>
        <div className="tsp-form-field">
          <label htmlFor="tsp-stop-location-lng">Longitude</label>
          <input id="tsp-stop-location-lng" type="text" inputMode="decimal" className="tsp-input" placeholder={loading ? "Fetching…" : "e.g. 75.0582"} value={draft.longitude} onChange={change("longitude")} disabled={busy} />
        </div>
      </div>
      <p className="tsp-muted-text tsp-stop-location-hint">
        {loading ? "Fetching the location of this place…" : "Fetched automatically — you can change the values before saving."}{" "}
        {!loading && (
          <button type="button" className="tsp-icon-link-btn tsp-stop-location-refetch" onClick={fetchPoint} disabled={busy}>
            <RefreshCw size={12} aria-hidden="true" /> Fetch again
          </button>
        )}
      </p>
      {approximate && <p className="tsp-muted-text">This town was not found on the map, so its district's location is shown.</p>}
      {error && (
        <p className="tsp-field-error" role="alert">
          {error}
        </p>
      )}
    </TspModal>
  );
}
